import type { DropdownMenuOption } from 'src/app/components/lib/dropdown';

export const parseSelectedIds = (values: string): number[] => {
  if (!values || values.trim() === '') {
    return [];
  }
  return values.split(',').map((id) => Number(id));
};

export const parseSelectedValues = (values: string): string[] => {
  if (!values || values.trim() === '') {
    return [];
  }
  return values.split(',');
};

type LookupKey = number | string;

const mapToDropdownOptions = <T>(
  selectedIds: LookupKey[],
  allOptions: T[],
  getKey: (option: T) => LookupKey,
  mapToDropdown: (option: T) => DropdownMenuOption
): DropdownMenuOption[] =>
  selectedIds
    .map((id) => {
      const option = allOptions.find((opt) => getKey(opt) === id);
      return option ? mapToDropdown(option) : null;
    })
    .filter((option): option is DropdownMenuOption => option !== null);

type ParseFunction = (values: string) => (number | string)[];

export const getMultiSelectedOptions = <T>(
  values: string,
  allOptions: T[],
  parseFn: ParseFunction,
  getKey: (option: T) => number | string,
  mapToDropdown: (option: T) => DropdownMenuOption
): DropdownMenuOption[] => {
  const selectedIds = parseFn(values);
  return mapToDropdownOptions(selectedIds, allOptions, getKey, mapToDropdown);
};

export function parseSelectedValuesGeneric(values: string): (number | string)[] {
  if (!values || values.trim() === '') {
    return [];
  }
  return values
    .split(',')
    .map((value) => {
      const trimmedValue = value.trim();
      // Try to convert to number if possible, otherwise keep as string
      const numberValue = Number(trimmedValue);
      return Number.isNaN(numberValue) ? trimmedValue : numberValue;
    })
    .filter((value) => value !== '');
}

export function getMultiSelectedOptionsGeneric<T>(
  values: string,
  allOptions: T[],
  parseValues: (values: string) => (number | string)[],
  getId: (option: T) => number | string,
  mapToDropdownOption: (option: T) => DropdownMenuOption
): DropdownMenuOption[] {
  const selectedValues = parseValues(values);
  if (!selectedValues || selectedValues.length === 0) {
    return [];
  }
  return allOptions
    .filter((option) => {
      const optionId = getId(option);
      return selectedValues.some((value) => value.toString() === optionId.toString());
    })
    .map(mapToDropdownOption);
}
